import {
  AWSMPCustomer,
  AuthUser,
  BillingData,
  Corpus,
  Customer,
  CustomerStatus,
  StatsModels,
  FailedDoc,
  Plan,
  PromptConfigParams,
  Stats,
  PricingPlanDetails,
  PlanParameters,
  User,
  Role,
  Permission,
  CustomerEnvironment
} from "../utils/commonTypes";
import { config } from "../config";
import { handleErrors } from "../utils/errorHandler";
import { removeAuthTokenFromStorage } from "../utils/storage";

const API_BACKEND = config.apiEndpoint;

export const listCorpora = async (customerId: number, authToken: string): Promise<Array<Corpus>> => {
  if (authToken === "") throw new Error("No auth token provided");
  const response = await fetch(`${API_BACKEND}/list-corpora`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    },
    body: JSON.stringify({ "customer-id": customerId })
  });
  if (response.status !== 200) {
    handleErrors(response.status, response.statusText);
    throw new Error(response.statusText);
  }
  const jsonData = await response.json();
  const corpora = jsonData.corpora.map((item: Record<string, unknown>) => {
    const itemObj = JSON.parse(item.toString());
    return { corpusId: itemObj["corpusId"], corpusName: itemObj["name"], enabled: itemObj["enabled"] };
  });
  return corpora;
};

export const listJobs = async (customerId: number, authToken: string) => {
  if (authToken === "") throw new Error("No auth token provided");
  const response = await fetch(`${API_BACKEND}/list-jobs`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    },
    body: JSON.stringify({ "customer-id": customerId })
  });
  if (response.status !== 200) {
    handleErrors(response.status, response.statusText);
    throw new Error(response.statusText);
  }
  const jsonData = await response.json();
  const jobs = jsonData.jobs.map((job: Record<string, unknown>) => {
    const jobObj = JSON.parse(job.toString());
    return {
      jobId: jobObj["jobId"],
      typeId: jobObj["typeId"],
      corpusId: jobObj["corpusId"],
      description: jobObj["desc"],
      params: jobObj["params"],
      status: jobObj["status"],
      comment: jobObj["comment"]
    };
  });
  return jobs;
};

export const readPricingPlans = async (customerId: number, authToken: string): Promise<Array<Plan>> => {
  if (authToken === "") throw new Error("No auth token provided");
  const response = await fetch(`${API_BACKEND}/read-pricing-plan`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    },
    body: JSON.stringify({ "customer-id": customerId })
  });
  if (response.status !== 200) {
    handleErrors(response.status, response.statusText);
    throw new Error(response.statusText);
  }
  const jsonData = await response.json();
  const plans = jsonData.plans.map((item: Record<string, any>): Plan => {
    const itemObj = JSON.parse(item.toString());
    const planObj = {
      planId: itemObj.planId,
      planName: itemObj.planName,
      planDescription: itemObj.description,
      planJson: itemObj.planJson,
      startDate: itemObj.dtEffective,
      expiryDate: itemObj.dtExpire,
      planType: itemObj.planType
    };
    return planObj;
  });

  return plans;
};

export const deleteCustomer = async (customerId: number, authToken: string): Promise<boolean> => {
  if (authToken === "") throw new Error("No auth token provided");
  const response = await fetch(`${API_BACKEND}/delete-customer`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    },
    body: JSON.stringify({ "customer-id": customerId })
  });
  if (response.status !== 200) {
    handleErrors(response.status, response.statusText);
    throw new Error(response.statusText);
  }
  return true;
};

export const purgeCustomer = async (customerId: number, authToken: string): Promise<boolean> => {
  if (authToken === "") throw new Error("No auth token provided");
  const response = await fetch(`${API_BACKEND}/purge-customer`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    },
    body: JSON.stringify({ "customer-id": customerId })
  });
  if (response.status !== 200) {
    handleErrors(response.status, response.statusText);
    throw new Error(response.statusText);
  }
  return true;
};

export const readCustomerStats = async (
  customerId: number,
  authToken: string,
  numMonths: number,
  anchorBased: boolean
): Promise<Array<Stats>> => {
  if (authToken === "") throw new Error("No auth token provided");
  const response = await fetch(`${API_BACKEND}/read-customer-stats`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    },
    body: JSON.stringify({ "customer-id": customerId, "no-of-months": numMonths, "anchor-based": anchorBased })
  });
  if (response.status !== 200) {
    handleErrors(response.status, response.statusText);
    throw new Error(response.statusText);
  }
  const jsonData = await response.json();
  const stats = jsonData.stats.map((stat: Record<string, unknown>): Stats => {
    const statsObj = JSON.parse(stat.toString());

    return {
      queryCount: statsObj.queries,
      summarizations: statsObj.modelSummarizationCount[StatsModels.OTHERS] ?? 0,
      gpt4GenerativeRequests: statsObj.modelSummarizationCount[StatsModels.GPT_4] ?? 0,
      bytesIngested: statsObj.bytes,
      storageUsed: statsObj.storage,
      users: statsObj.users,
      corpora: statsObj.corpora,
      month: statsObj.month,
      year: statsObj.year,
      startEpoch: statsObj.startEpochSecs,
      endEpoch: statsObj.endEpochSecs
    };
  });
  return stats;
};

export const authorizeUser = async (idTokenString: string): Promise<AuthUser> => {
  const response = await fetch(`${API_BACKEND}/read-user-authorization`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ idTokenString: idTokenString })
  });
  if (response.status !== 200) {
    // Failed to authenticate
    removeAuthTokenFromStorage();
    console.log(response);
    throw new Error(response.statusText);
  }
  const jsonData = await response.json();
  return {
    email: jsonData["username"],
    role: jsonData["role"],
    permissions: jsonData.permissions
  };
};

export const listEncoders = async (customerId: number, authToken: string) => {
  if (authToken === "") throw new Error("No auth token provided");
  const response = await fetch(`${API_BACKEND}/list-encoders`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    },
    body: JSON.stringify({ "customer-id": customerId })
  });
  if (response.status !== 200) {
    handleErrors(response.status, response.statusText);
    throw new Error(response.statusText);
  }
  const jsonData = await response.json();
  const encoders = jsonData.encoders.map((encoder: Record<string, unknown>) => {
    const encoderObj = JSON.parse(encoder.toString());
    return {
      name: encoderObj["name"],
      description: encoderObj["description"],
      dimension: encoderObj["dimension"],
      cxSpec: encoderObj["cxSpec"],
      cxSpecServing: encoderObj["cxSpecServing"],
      default: encoderObj["defaultEncoder"]
    };
  });
  return encoders;
};

export const createEncoder = async (
  customerId: number,
  authToken: string,
  name: string,
  description: string,
  dimension: number,
  host: string,
  port: number
): Promise<boolean> => {
  if (authToken === "") throw new Error("No auth token provided");
  const response = await fetch(`${API_BACKEND}/create-encoder`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    },
    body: JSON.stringify({
      "customer-id": customerId,
      name: name,
      description: description,
      dimension: dimension,
      host: host,
      port: port
    })
  });
  if (response.status !== 200) {
    const detail = await response.text();
    handleErrors(response.status, detail);
    throw new Error(detail);
  }
  return true;
};

export const listPrompts = async (customerId: number, authToken: string) => {
  if (authToken === "") throw new Error("No auth token provided");
  const response = await fetch(`${API_BACKEND}/list-prompts`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    },
    body: JSON.stringify({ "customer-id": customerId })
  });
  if (response.status !== 200) {
    handleErrors(response.status, response.statusText);
    throw new Error(response.statusText);
  }
  const jsonData = await response.json();
  const prompts = jsonData.prompts.map((prompt: Record<string, unknown>) => {
    const promptObj = JSON.parse(prompt.toString());
    const modelDef = promptObj["modelDef"];
    const promptDef = promptObj["summarizationPromptDef"];
    return {
      name: promptDef["name"],
      description: promptDef["description"],
      model: { modelId: modelDef["id"]["value"], modelName: modelDef["externalName"] },
      prompt: promptDef["prompt"],
      configParams: promptDef["configParams"],
      isDefault: promptDef["isDefault"]
    };
  });
  return prompts;
};

export const createPrompt = async (
  customerId: number,
  authToken: string,
  name: string,
  description: string,
  modelId: number,
  modelName: string,
  promptType: string,
  prompt: string,
  testOnly: boolean,
  configParams: PromptConfigParams
): Promise<boolean> => {
  if (authToken === "") throw new Error("No auth token provided");
  const response = await fetch(`${API_BACKEND}/create-prompt`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    },
    body: JSON.stringify({
      customerId: customerId,
      name: name,
      description: description,
      modelId: modelId,
      modelName: modelName,
      promptType: promptType,
      prompt: prompt,
      testOnly: testOnly,
      configParams: JSON.stringify(configParams)
    })
  });
  if (response.status !== 200) {
    const detail = await response.text();
    handleErrors(response.status, detail);
    throw new Error(detail);
  }
  return true;
};

export const listAWSMPCustomers = async (authToken: string): Promise<Array<AWSMPCustomer>> => {
  if (authToken === "") throw new Error("No auth token provided");
  const response = await fetch(`${API_BACKEND}/list-awsmp-customers`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    },
    body: JSON.stringify({})
  });
  if (response.status !== 200) {
    handleErrors(response.status, response.statusText);
    throw new Error(response.statusText);
  }
  const jsonData = await response.json();
  const customers = jsonData.customers.map((item: Record<string, unknown>): AWSMPCustomer => {
    const itemObj = JSON.parse(item.toString());
    return {
      customerId: itemObj["customerId"],
      name: itemObj["name"],
      userEmail: itemObj["root_email"],
      awsId: itemObj["awsId"],
      awsAccountId: itemObj["awsAccountId"],
      tsContractStart: itemObj["tsContractStart"],
      tsContractExpire: itemObj["tsContractExpire"],
      awsProductCode: itemObj["awsProductCode"],
      planId: itemObj["customerPlanId"],
      planName: itemObj["customerPlanName"]
    };
  });
  return customers;
};

export const listFailedDocs = async (
  authToken: string,
  customerId: number,
  limit: number,
  documentId: string
): Promise<Array<FailedDoc>> => {
  if (authToken === "") throw new Error("No auth token provided");
  const response = await fetch(`${API_BACKEND}/list-failed-docs`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    },
    body: JSON.stringify({ "customer-id": customerId, limit: limit, "document-id": documentId })
  });
  if (response.status !== 200) {
    handleErrors(response.status, response.statusText);
    throw new Error(response.statusText);
  }
  const jsonData = await response.json();
  const failedDocs = jsonData.failedDocs.map((item: Record<string, unknown>): FailedDoc => {
    const itemObj = JSON.parse(item.toString());
    const locationObj = JSON.parse(itemObj.location.toString());
    return {
      id: itemObj.id,
      documentId: itemObj.documentId,
      customerId: itemObj.customerCorpusKey.customerId,
      corpusId: itemObj.customerCorpusKey.corpusId,
      originalStatusCode: itemObj.originalStatus.code_,
      originalStatusText: itemObj.originalStatus.statusDetail_,
      mimeType: itemObj.mimeType,
      locationType: locationObj.type,
      locationPath: locationObj.path,
      locationBucket: locationObj.bucket,
      api: itemObj.api
    };
  });
  return failedDocs;
};

export const downloadFailedDoc = async (authToken: string, failedDoc: FailedDoc): Promise<boolean> => {
  if (authToken === "") throw new Error("No auth token provided");
  const response = await fetch(`${API_BACKEND}/download-failed-doc`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    },
    body: JSON.stringify({
      id: failedDoc.id,
      docId: failedDoc.documentId,
      location: failedDoc.locationPath,
      bucket: failedDoc.locationBucket,
      customerId: failedDoc.customerId,
      corpusId: failedDoc.corpusId
    })
  });
  if (response.status !== 200) {
    const detail = await response.text();
    handleErrors(response.status, detail);
    throw new Error(detail);
  }
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = failedDoc.documentId + ".tar";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
  return true;
};

export const searchCustomers = async (authToken: string, searchTerm: string, statuses: CustomerStatus[] = [CustomerStatus.ACT]): Promise<Array<Customer>> => {
  if (authToken === "") throw new Error("No auth token provided");

  const response = await fetch(`${API_BACKEND}/search-customers`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    },
    body: JSON.stringify({
      "operational-statuses": statuses,
      "search-term": searchTerm
    })
  });
  if (response.status !== 200) {
    handleErrors(response.status, response.statusText);
    throw new Error(response.statusText);
  }
  const jsonData = await response.json();

  const customers = jsonData.customers.map((item: Record<string, unknown>) => {
    return {
      customerId: item.customerId,
      userName: item.name,
      userEmail: item.root_email,
      status: CustomerStatus[item.operationalStatus as CustomerStatus],
      tableExtractionEnabled: item.tableExtractionEnabled
    };
  });

  return customers;
};

export const getCustomerById = async (authToken: string, customerId: number): Promise<Customer | null> => {
  const allStatuses = Object.values(CustomerStatus);
  const customers = await searchCustomers(authToken, customerId.toString(), allStatuses);
  return customers.find((c) => Number(c.customerId) === customerId) ?? null;
};

export const updateCustomerPlan = async (
  authToken: string,
  customerId: number,
  planId: string,
  planSpec: any,
  planParameters: PlanParameters
) => {
  if (authToken === "") throw new Error("No auth token provided");

  const response = await fetch(`${API_BACKEND}/switch-customer-plan`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    },
    body: JSON.stringify({
      customerId,
      planId,
      planSpec: JSON.stringify(planSpec),
      planParameters: JSON.stringify(planParameters)
    })
  });

  if (response.status !== 200) {
    const text = await response.text();
    handleErrors(response.status, text);
    throw new Error(text);
  }
};

export const changeCustomerState = async (
  authToken: string,
  customerId: number,
  targetStatus: CustomerStatus,
  reason: string
): Promise<void> => {
  if (authToken === "") throw new Error("No auth token provided");

  const response = await fetch(`${API_BACKEND}/change-customer-state`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    },
    body: JSON.stringify({ customerId, targetStatus, reason })
  });

  if (response.status !== 200) {
    const text = await response.text();
    handleErrors(response.status, text);
    throw new Error(text);
  }
};

export const readPricingPlanDetails = async (
  authToken: string,
  planId: string,
  customerId = 0
): Promise<PricingPlanDetails> => {
  if (authToken === "") throw new Error("No auth token provided");

  const response = await fetch(`${API_BACKEND}/read-pricing-plan-details`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    },
    body: JSON.stringify({
      planId,
      customerId
    })
  });

  if (response.status !== 200) {
    const text = await response.text();
    handleErrors(response.status, text);
    throw new Error(text);
  }

  const responseJson = await response.json();
  return { ...responseJson, planSpec: JSON.parse(responseJson.planSpec) };
};

// User Management API calls
export const listUsers = async (authToken: string): Promise<User[]> => {
  if (authToken === "") throw new Error("No auth token provided");

  const response = await fetch(`${API_BACKEND}/list-users`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    }
  });

  if (response.status !== 200) {
    handleErrors(response.status, response.statusText);
    throw new Error(response.statusText);
  }

  const jsonData = await response.json();
  return jsonData.users;
};

export const createUser = async (
  authToken: string,
  user: string,
  organization: string,
  description: string,
  roles: string[]
): Promise<{ success: boolean; userId: number }> => {
  if (authToken === "") throw new Error("No auth token provided");

  const response = await fetch(`${API_BACKEND}/create-user`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    },
    body: JSON.stringify({
      user,
      organization,
      description,
      roles
    })
  });

  if (response.status !== 200) {
    const detail = await response.text();
    handleErrors(response.status, detail);
    throw new Error(detail);
  }

  return await response.json();
};

export const deleteUser = async (authToken: string, userId: number): Promise<{ success: boolean }> => {
  if (authToken === "") throw new Error("No auth token provided");

  const response = await fetch(`${API_BACKEND}/delete-user`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    },
    body: JSON.stringify({
      userId
    })
  });

  if (response.status !== 200) {
    const detail = await response.text();
    handleErrors(response.status, detail);
    throw new Error(detail);
  }

  return await response.json();
};

export const updateUserRoles = async (
  authToken: string,
  userId: number,
  roles: string[]
): Promise<{ success: boolean }> => {
  if (authToken === "") throw new Error("No auth token provided");
  if (userId === -1) throw new Error("No user ID provided");

  const response = await fetch(`${API_BACKEND}/update-user-roles`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    },
    body: JSON.stringify({
      userId,
      roles
    })
  });

  if (response.status !== 200) {
    const detail = await response.text();
    handleErrors(response.status, detail);
    throw new Error(detail);
  }

  return await response.json();
};

export const listRoles = async (authToken: string): Promise<Role[]> => {
  if (authToken === "") throw new Error("No auth token provided");

  const response = await fetch(`${API_BACKEND}/list-roles`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    }
  });

  if (response.status !== 200) {
    handleErrors(response.status, response.statusText);
    throw new Error(response.statusText);
  }

  const jsonData = await response.json();
  return jsonData.roles;
};

export const listPermissions = async (authToken: string): Promise<Permission[]> => {
  if (authToken === "") throw new Error("No auth token provided");

  const response = await fetch(`${API_BACKEND}/list-permissions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    }
  });

  if (response.status !== 200) {
    handleErrors(response.status, response.statusText);
    throw new Error(response.statusText);
  }

  const jsonData = await response.json();
  return jsonData.permissions;
};

export const updateRolePermissions = async (
  authToken: string,
  roleId: number,
  permissions: string[]
): Promise<{ success: boolean }> => {
  if (authToken === "") throw new Error("No auth token provided");
  if (roleId === -1) throw new Error("No role ID provided");

  const response = await fetch(`${API_BACKEND}/update-role-permissions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    },
    body: JSON.stringify({
      roleId,
      permissions
    })
  });

  if (response.status !== 200) {
    const detail = await response.text();
    handleErrors(response.status, detail);
    throw new Error(detail);
  }

  return await response.json();
};

export const readCustomerEnvironment = async (authToken: string, customerId: number): Promise<CustomerEnvironment> => {
  if (authToken === "") throw new Error("No auth token provided");

  const response = await fetch(`${API_BACKEND}/read-customer-environment`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    },
    body: JSON.stringify({ customerId })
  });

  if (response.status !== 200) {
    handleErrors(response.status, response.statusText);
    throw new Error(response.statusText);
  }

  return response.json();
};

export const readBillingData = async (customerId: number, authToken: string): Promise<BillingData> => {
  if (authToken === "") throw new Error("No auth token provided");
  
  const response = await fetch(`${API_BACKEND}/read-billing-data`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: authToken
    },
    body: JSON.stringify({ "customer-id": customerId })
  });
  
  if (response.status !== 200) {
    handleErrors(response.status, response.statusText);
    throw new Error(response.statusText);
  }
  
  const data = await response.json();
  
  // Transform the record-based response to match our frontend interface
  const transformedData: BillingData = {
    billing_periods: data.billingPeriods.map((period: any) => ({
      id: period.id,
      customer_id: period.customerId,
      period_start: period.periodStart,
      period_end: period.periodEnd,
      amount_due: 0, // We'll need to calculate this from billing period details
      currency: "USD", // Default currency
      status: period.status,
      stripe_invoice_id: period.stripeInvoiceId,
      stripe_payment_id: period.stripePaymentId,
      comment: period.comment,
      created_at: period.createdAt,
      updated_at: period.updatedAt
    })),
    billing_period_details: data.billingPeriodDetails.map((detail: any) => ({
      id: detail.id,
      billing_period_id: detail.billingPeriodId,
      customer_id: detail.customerId,
      pricing_agreement_id: detail.pricingAgreementId,
      dt_start: detail.dtStart,
      dt_end: detail.dtEnd,
      item_description: detail.itemDescription,
      amount: detail.amount,
      currency: detail.currency,
      comment: detail.comment,
      created_at: detail.createdAt,
      updated_at: detail.updatedAt,
      customer_plan_id: detail.customerPlanId,
      plan_type: detail.planType,
      quantity: 1.0, // Default value
      unit_price: detail.amount // Same as amount since quantity is 1
    })),
    scheduled_payments: data.scheduledPayments.map((payment: any) => ({
      id: payment.id,
      customer_id: payment.customerId,
      scheduled_date: payment.scheduledDate,
      processed_date: payment.processedDate,
      amount: payment.amount,
      currency: payment.currency,
      status: payment.status,
      description: payment.comment,
      comment: payment.comment,
      billing_period_id: payment.billingPeriodId,
      attempt_ctr: payment.attemptCtr,
      retry_ctr: payment.retryCtr,
      created_at: payment.createdAt,
      updated_at: payment.updatedAt
    }))
  };
  
  // Calculate amounts for billing periods using related details
  const periodTotals = new Map<number, { total: number, currency: string }>();
  
  transformedData.billing_period_details.forEach(detail => {
    const periodId = detail.billing_period_id;
    if (!periodTotals.has(periodId)) {
      periodTotals.set(periodId, { total: 0, currency: detail.currency });
    }
    const current = periodTotals.get(periodId)!;
    current.total += detail.amount;
  });
  
  transformedData.billing_periods.forEach(period => {
    const totals = periodTotals.get(period.id);
    if (totals) {
      period.amount_due = totals.total;
      period.currency = totals.currency;
    }
  });
  
  return transformedData;
}